import axios from 'axios';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  normalizeMfaChallenge,
  type LoginResponse,
  type MfaRequiredPayload,
  type MfaSetupRequiredPayload,
} from '../types/authMfa';
import { SET_USER } from '../utils/constants/action-types';
import { APP_ROUTES } from '../utils/constants/appRoutes';
import { decodeJwtToken } from '../utils/helpers/validateuser';
import { getSafeRedirectPath } from '../utils/redirectAfterLogin';
import { setToken } from '../utils/security/tokenStorage';

/**
 * Validates a Google ID token with Wireless-API and continues the same session
 * pipeline as password / Entra login (JWT and/or MFA).
 */
export const useGoogleLogin = (
  setLoadingGoogle: (loading: boolean) => void,
  setError: (message: string) => void,
  redirectPath: string = APP_ROUTES.HOME,
  onMfaRequired?: (payload: MfaRequiredPayload) => void,
  onMfaSetupRequired?: (payload: MfaSetupRequiredPayload) => void
) => {
  const dispatch = useDispatch();
  const API_ADDRESS = process.env.REACT_APP_API_ADDRESS;
  const navigate = useNavigate();

  return useCallback(
    async (googleIdToken: string) => {
      setLoadingGoogle(true);
      try {
        const googleResponse = await axios
          .create({
            baseURL: API_ADDRESS,
            headers: { Authorization: `Bearer ${googleIdToken}` },
          })
          .post<LoginResponse>('api/auth/validate-google', {});

        const data = googleResponse.data;

        if ('mfaRequired' in data && data.mfaRequired) {
          onMfaRequired?.({
            mfaContextToken: data.mfaContextToken,
            mfaChallenge: normalizeMfaChallenge(data.mfaChallenge),
          });
          return;
        }

        if ('mfaSetupRequired' in data && data.mfaSetupRequired && data.accessToken) {
          onMfaSetupRequired?.({ accessToken: data.accessToken });
          return;
        }

        if ('accessToken' in data && data.accessToken) {
          try {
            setToken(data.accessToken);
            const userData = decodeJwtToken(data.accessToken);
            dispatch({ type: SET_USER, payload: userData });
            const safePath = getSafeRedirectPath(redirectPath, APP_ROUTES.HOME);
            navigate(safePath);
          } catch (tokenError) {
            console.error('[GoogleLogin] Failed to store token securely:', tokenError);
            setError('Failed to store authentication token. Please try again.');
          }
          return;
        }

        setError('Unexpected response from Google sign-in. Please try again.');
      } catch (error: unknown) {
        console.error(error);
        const err = error as { response?: { data?: { message?: string } } };
        const message = err.response?.data?.message;
        if (message) {
          setError(message);
        } else {
          setError('Cannot login with Google. Please contact the administrator.');
        }
      } finally {
        setLoadingGoogle(false);
      }
    },
    [
      API_ADDRESS,
      dispatch,
      navigate,
      onMfaRequired,
      onMfaSetupRequired,
      redirectPath,
      setError,
      setLoadingGoogle,
    ]
  );
};
